import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { MANUFACTURER_REQUEST_STATUSES } from "models";
import "modules/ManufacturerDashboard/components/ManufacturerRequestModal/ManufacturerRequestModal.scss";
import { useManufacturerRequestMutation } from "modules/ManufacturerDashboard/hooks";

import type { ManufacturerRequest } from "models";

type ManufacturerRequestModalPropTypes = {
  selectedRequest: ManufacturerRequest | null;
  onCloseModal: () => void;
};

export const ManufacturerRequestModal = ({ onCloseModal, selectedRequest }: ManufacturerRequestModalPropTypes) => {
  const { t } = useTranslation();

  const [declineReason, setDeclineReason] = useState("");
  const [isApproving, setIsApproving] = useState(false);

  const { mutateAsync: updateRequest, isPending } = useManufacturerRequestMutation();

  if (!selectedRequest) return null;

  const handleUpdate = async (manufacturer_request_status: MANUFACTURER_REQUEST_STATUSES, decline_reason?: string) => {
    setIsApproving(manufacturer_request_status === MANUFACTURER_REQUEST_STATUSES.Approved);
    await updateRequest({ manufacturer_request_id: selectedRequest.id, manufacturer_request_status, decline_reason });
    setDeclineReason("");
    onCloseModal();
  };

  const handlerForApproval = () => handleUpdate(MANUFACTURER_REQUEST_STATUSES.Approved);
  const handlerForDecline = () => handleUpdate(MANUFACTURER_REQUEST_STATUSES.Declined, declineReason);

  return (
    <Modal open={Boolean(selectedRequest)} size="tiny">
      <Modal.Header className="Manufacturer__RequestModal-header">
        <span>{t("v8_confirmation_required", "Confirmation required")}</span>
      </Modal.Header>
      <Modal.Content className="Manufacturer__RequestModal-content">
        <span>{t("v8_approve_or_decline_manufacturer_request", "Once you approved or declined this request, you can't undo this action.")}</span>
        <span>
          <b>{t("v8_manufacturer", "Manufacturer")}</b>: {selectedRequest.manufacturer?.first_name} {selectedRequest.manufacturer?.last_name}
        </span>
        <span>
          <b>{t("v8_reason", "Reason")}</b>: {selectedRequest.request_reason}
        </span>
        <b>{t("v8_enter_a_reason_to_decline", "Please enter a reason if you want to decline this request")}</b>
        <textarea onChange={e => setDeclineReason(e.target.value)} value={declineReason} className="Manufacturer__RequestModal-content--textarea" />
      </Modal.Content>
      <Modal.Actions className="Manufacturer__RequestModal-actions">
        <Button onClick={onCloseModal} basic disabled={isPending}>
          {t("v8_cancel", "Cancel")}
        </Button>
        <Button onClick={handlerForDecline} basic disabled={!declineReason || isPending} loading={isPending && !isApproving}>
          {t("v8_decline", "Decline")} <Icon className="x" />
        </Button>
        <Button onClick={handlerForApproval} className="Manufacturer__RequestModal-actions--approve" disabled={isPending} loading={isPending && isApproving}>
          {t("v8_approve", "Approve")} <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
